// Reads the subscription again without re-initialising the app.
//
// `fetchAppInit` first marks the session as not initialised
// (`setIsInitSuccess(false)`), which brings back the full-screen splash and
// remounts the routed page. Waiting for a paid plan to show after a checkout
// must not do that, so this calls seller-init itself and applies only the
// plan state and the Tara subscription.

import store from '../../../store/store';
import { INIT_API } from '../../constants/central-api';
import { applyPlanState } from '../../../store/reducers/authSlice';
import {
  setTaraSubscriptionDetails,
  setTaraTrialDetails,
} from '../../../store/reducers/userSlice';
import isNotEmptyString from '../../helper-functions/isNotEmptyString';

/** The Tara subscription row seller-init's `subscription` list stands for:
 *  a paid plan wins over a trial. Mirrors the app-init state update. */
export function pickTaraSubscription(subscription: unknown) {
  if (!Array.isArray(subscription)) return null;
  const rows = subscription.filter(
    (item: any) =>
      item?.subscription_type_slug === 'tara-ai-services' &&
      item?.status === 'success'
  );
  return (
    rows.find((item: any) => item.is_trial === false) ??
    rows.find((item: any) => item.is_trial === true) ??
    null
  );
}

/** The trial state seller-init's `seller` stands for. Same rule as the
 *  app-init state update, so a trial that converted to a paid plan clears
 *  the trial UI (brand limit, sidebar countdown) without a reload. */
export function pickTaraTrialDetails(seller: any) {
  return seller?.is_trial
    ? { is_trial: true, trial_available_days: seller?.trial_available_days }
    : { is_trial: false, trial_available_days: undefined };
}

/** Resolves to true when the request succeeded and was applied. Never shows
 *  the app loader, never logs out; a failure simply leaves the state as is. */
export default async function refresh_tara_subscription_quietly(): Promise<boolean> {
  const state = store.getState();
  const accessToken = state.auth.authorization.accessToken;
  const token = state.UI?.isWhiteLabel
    ? accessToken?.currentWhiteLabelSellerToken
    : accessToken?.sellerToken;
  if (!isNotEmptyString(token)) return false;

  try {
    const response = await fetch(INIT_API, {
      method: 'GET',
      headers: { Authorization: `${accessToken.type} ${token}` },
    });
    const res = await response.json();
    if (!res?.success || !res?.data) return false;

    store.dispatch(applyPlanState(res.data));
    store.dispatch(setTaraTrialDetails(pickTaraTrialDetails(res.data.seller)));
    const current = pickTaraSubscription(res.data.subscription);
    // Only an actual row is applied: the page is waiting for one, and an
    // empty answer must not wipe the plan it already shows.
    if (current) {
      store.dispatch(
        setTaraSubscriptionDetails({
          ...current,
          currentSubscriptionStatus: 'active',
        })
      );
    }
    return true;
  } catch {
    return false;
  }
}
